import React, { useState } from "react";
import { RLink } from "../DesignSystem/RLink";
import { type QueuedSong, removeSongFromQueue } from "../state";
import { PlaylistPopover, PlaylistSong } from "./PlaylistPopover";

interface PlayerSongProps {
  toggleLoop: () => void;
  changeSongIndex: (newSongIndex: number) => void;
  currentSongIndex: number;
  looping: boolean;
  songs: QueuedSong[];
  onNav?: () => void;
  playlistVisible?: boolean;
  onTogglePlaylist?: () => void;
  hideControls?: boolean;
  hideQueueButton?: boolean;
}

export function AudioPlayerSong({
  changeSongIndex,
  currentSongIndex,
  songs,
  onNav,
  playlistVisible: playlistVisibleProp,
  onTogglePlaylist,
  hideControls,
  hideQueueButton,
}: PlayerSongProps): JSX.Element {
  const [playlistVisibleState, setPlaylistVisibleState] = useState(false);

  const playlistVisible = playlistVisibleProp ?? playlistVisibleState;

  const togglePlaylist = () => {
    if (onTogglePlaylist) {
      onTogglePlaylist();
    } else {
      setPlaylistVisibleState((prev) => !prev);
    }
  };

  const currentSong =
    currentSongIndex >= 0 && currentSongIndex < songs.length
      ? songs[currentSongIndex]
      : undefined;

  const songName = currentSong ? currentSong.name : "No song selected";

  // Convert QueuedSong[] to PlaylistSong[]
  const playlistSongs: PlaylistSong[] = songs.map((song) => ({
    entry_id: song.entry_id,
    name: song.name,
    uploaded_url: song.uploaded_url,
    entry_url: song.entry_url,
    artists: song.artists,
  }));

  return (
    <div className="relative flex min-w-[220px] flex-1 flex-col gap-1 text-left">
      {playlistVisible && (
        <div className="absolute bottom-full left-0 z-50 mb-2">
          <PlaylistPopover
            songs={playlistSongs}
            currentIndex={currentSongIndex}
            onSongClick={changeSongIndex}
            onRemove={removeSongFromQueue}
            title="Playlist"
            showAddRandom
          />
        </div>
      )}

      {!hideControls && (
        <>
          <div className="flex items-center gap-2">
            {currentSong ? (
              <RLink
                white
                className="text-base font-semibold text-slate-900 hover:text-chips-gold dark:text-white"
                to={currentSong.entry_url}
                onClick={() => (onNav ? onNav() : undefined)}
              >
                {songName.length > 200
                  ? songName.slice(0, 200) + "..."
                  : songName}
              </RLink>
            ) : (
              <span className="text-sm font-normal text-slate-500 dark:text-white/50">
                {songName}
              </span>
            )}
            {!hideQueueButton && (
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-transparent text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:border-white/10 dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white"
                onClick={togglePlaylist}
                aria-label="Show playlist"
              >
                <i className="fa fa-list-ul" aria-hidden="true"></i>
              </button>
            )}
          </div>
          {currentSong?.artists && currentSong.artists.length > 0 && (
            <div className="text-xs text-slate-500 dark:text-white/70">
              by{" "}
              {currentSong.artists.map((username, i) => (
                <span key={username}>
                  {i > 0 && ", "}
                  <RLink
                    to={`/user/${username}`}
                    className="hover:text-chips-gold"
                  >
                    {username}
                  </RLink>
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
